<template>
  <div class="signup-register">
    <ProgressDots
      v-if="!awaitingEmailVerification"
      :total="TOTAL_STEPS"
      :current="step"
      :aria-label="t('progress_aria')"
    />

    <div class="signup-body">
      <VerifyEmailStep
        v-if="awaitingEmailVerification"
        :email="form.email"
        @email-changed="form.email = $event"
      />

      <!-- Step 1: Birthday + social login -->
      <BirthdayStep
        v-show="!awaitingEmailVerification && step === 1"
        :class="{ active: step === 1 }"
        :day.sync="form.day"
        :month.sync="form.month"
        :year.sync="form.year"
      />

      <!-- Step 2: Location -->
      <LocationStep
        v-show="!awaitingEmailVerification && step === 2"
        :class="{ active: step === 2 }"
        :location-id.sync="form.locationId"
        :location-label.sync="form.locationLabel"
      />

      <!-- Step 3: Gender + preferred gender + age range -->
      <MatchingStep
        v-show="!awaitingEmailVerification && step === 3"
        :class="{ active: step === 3 }"
        :gender.sync="form.gender"
        :preferred-gender.sync="form.preferredGender"
        :age-from.sync="form.ageFrom"
        :age-to.sync="form.ageTo"
      />

      <!-- Step 4: Intent -->
      <IntentStep
        v-show="!awaitingEmailVerification && step === 4"
        :class="{ active: step === 4 }"
        :preferences.sync="form.preferences"
      />

      <!-- Step 5: Account (name + email + password + terms). In continue
           mode the user already has an account, so email/password/terms are
           hidden and we only collect the missing display name. The :key gives
           the step a fresh instance per signup session so its own view state
           (password visibility) resets without the parent reaching in. -->
      <AccountStep
        :key="accountStepKey"
        v-show="!awaitingEmailVerification && step === 5"
        :class="{ active: step === 5 }"
        :name.sync="form.name"
        :email.sync="form.email"
        :password.sync="form.password"
        :terms.sync="form.terms"
        :errors="errors"
        :submit-error="submitError"
        :is-continuing="isContinuing"
      />
    </div>

    <div
      v-if="!awaitingEmailVerification"
      class="signup-foot"
    >
      <button
        type="button"
        class="sb-back"
        :class="{ hidden: step === 1 }"
        @click="prevStep"
      >
        {{ t('btn_back') }}
      </button>
      <button
        type="button"
        class="sb-next"
        :class="{ wide: step >= TOTAL_STEPS }"
        :disabled="isNextDisabled"
        @click="nextStep"
      >
        {{ nextButtonLabel }}
        <span v-if="step < TOTAL_STEPS" aria-hidden="true">→</span>
        <icon-spark v-else class="ic-spark" :size="16" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  i18nMixin,
  refreshCsrfToken,
  userAwaitingEmailVerification,
  EMAIL_RE,
  calcAge,
  isPasswordValid,
} from './helpers';
import {
  TOTAL_STEPS,
  clampAgeRange,
  INTENTS,
  PREFERENCE_ID_TO_SLUG,
  EMPTY_FORM,
} from './signupConfig';
import ProgressDots from './components/ProgressDots.vue';
import VerifyEmailStep from './components/VerifyEmailStep.vue';
import BirthdayStep from './components/BirthdayStep.vue';
import LocationStep from './components/LocationStep.vue';
import MatchingStep from './components/MatchingStep.vue';
import IntentStep from './components/IntentStep.vue';
import AccountStep from './components/AccountStep.vue';
import IconSpark from '../icons/IconSpark.vue';
import { STEP_NAMES, stashAnalyticsContext, trackEvent } from '../../../analytics/ga4';

export default {
  name: 'RegisterForm',
  mixins: [i18nMixin],
  components: {
    ProgressDots,
    VerifyEmailStep,
    BirthdayStep,
    LocationStep,
    MatchingStep,
    IntentStep,
    AccountStep,
    IconSpark,
  },
  props: {
    ctaLocation: { type: String, default: 'unknown' },
  },
  data() {
    return {
      TOTAL_STEPS,
      step: 1,
      submitting: false,
      submitError: '',
      form: EMPTY_FORM(),
      errors: {},
      // Bumped on reset() to remount AccountStep so its internal view state
      // (password visibility) starts fresh each signup session.
      accountStepKey: 0,
      // Shown after register when verification_is_required — UI lives in
      // VerifyEmailStep.vue (resend + change email).
      awaitingEmailVerification: false,
      // True when LandingPage detected the user is mid-registration (no
      // filled_at / location_id) and reopened the modal to finish their
      // existing account — see continueWithUser(). In this mode we pre-fill
      // from AuthUserResource, jump to the right step, hide the
      // email/password/terms inputs on step 5, and skip POST /register on
      // submit (the account already exists).
      isContinuing: false,
    };
  },
  computed: {
    // Read the public smart_message_categories catalog from the shared Vuex
    // module (front/store/modules/preferences). The legacy GenderCard /
    // PreferencesCard flow already populates it via the same dispatch — we
    // hook into the same source of truth instead of fetching ourselves.
    ...mapGetters('preferences', { preferencesCatalog: 'getPreferences' }),
    canAdvance() {
      switch (this.step) {
        case 1: {
          const age = calcAge(this.form.day, this.form.month, this.form.year);
          return age !== null && age >= 18 && age <= 100;
        }
        case 2:
          return !!this.form.locationId;
        case 3:
          return !!this.form.gender && !!this.form.preferredGender;
        case 4:
          return this.form.preferences.length > 0;
        case 5:
          // Step 5 validates on submit click — do not gate the button.
          return true;
        default:
          return false;
      }
    },
    isNextDisabled() {
      if (this.submitting) {
        return true;
      }
      if (this.step === TOTAL_STEPS) {
        return false;
      }
      return !this.canAdvance;
    },
    nextButtonLabel() {
      if (this.step >= TOTAL_STEPS) {
        return this.t('btn_finish');
      }
      if (this.step === 2) {
        return this.t('btn_take_location');
      }
      if (this.step === 3) {
        return this.t('btn_show_matches');
      }
      if (this.step === 4) {
        return this.t('btn_intent_confirm');
      }
      return this.t('btn_next');
    },
    // Parent uses this (via $refs) to decide whether closing should prompt.
    hasUserInput() {
      const f = this.form;
      return !!(
        f.day ||
        f.month ||
        f.year ||
        f.locationId ||
        f.gender ||
        f.preferredGender ||
        f.preferences.length ||
        f.name ||
        f.email ||
        f.password
      );
    },
    // Maps intent slugs to smart_message_categories IDs (catalog `name` is
    // translated; we match by id — see PREFERENCE_ID_TO_SLUG).
    preferenceSlugToId() {
      const map = {};
      for (const p of this.preferencesCatalog) {
        const slug = PREFERENCE_ID_TO_SLUG[p.id];
        if (slug) {
          map[slug] = p.id;
        }
      }
      return map;
    },
  },
  mounted() {
    // Cached inside the action — repeated dispatches are a no-op once loaded.
    this.$store.dispatch('preferences/load');
  },
  methods: {
    reset() {
      this.step = 1;
      this.form = EMPTY_FORM();
      this.errors = {};
      this.submitError = '';
      this.submitting = false;
      this.awaitingEmailVerification = false;
      this.isContinuing = false;
      // Force a fresh AccountStep so its password-visibility toggle resets.
      this.accountStepKey++;
    },
    // Called by SignupModal when LandingPage detects a partly-registered
    // user (auth_step in: birthday|location|gender|preferences|name).
    // Pre-fills the form from AuthUserResource and jumps to the matching
    // step so the user picks up exactly where they left off. submit() then
    // skips POST /register (account exists) and only sends PUT
    // /register/update.
    continueWithUser(user) {
      this.reset();

      if (userAwaitingEmailVerification(user) || user.auth_step === 'email') {
        if (user.email) {
          this.form.email = user.email;
        }
        this.awaitingEmailVerification = true;
        return;
      }

      this.isContinuing = true;

      // AuthUserResource formats birth_date with PHP's "d m Y" → "DD MM YYYY"
      // (zero-padded day + month). Split on whitespace and stuff each piece
      // into the per-digit inputs.
      if (user.birth_date) {
        const parts = String(user.birth_date).trim().split(/\s+/);
        if (parts.length === 3) {
          this.form.day = String(parseInt(parts[0], 10) || '');
          this.form.month = String(parseInt(parts[1], 10) || '');
          this.form.year = parts[2];
        }
      }
      if (user.location_id) {
        this.form.locationId = user.location_id;
      }
      if (user.gender) {
        this.form.gender = user.gender;
      }
      if (Array.isArray(user.preferred_gender) && user.preferred_gender.length) {
        // The pill row supports a single value ('male'|'female'|'all'); the
        // backend stores both as a CSV which AuthUserResource explodes.
        this.form.preferredGender =
          user.preferred_gender.length > 1 ? 'all' : user.preferred_gender[0];
      }
      if (user.search) {
        if (user.search.age_from) {
          this.form.ageFrom = parseInt(user.search.age_from, 10);
        }
        if (user.search.age_to) {
          this.form.ageTo = parseInt(user.search.age_to, 10);
        }
      }
      // Sanitize the prefilled range the same way MatchingStep's slider does.
      const range = clampAgeRange(this.form.ageFrom, this.form.ageTo);
      this.form.ageFrom = range.from;
      this.form.ageTo = range.to;
      if (Array.isArray(user.preferences) && user.preferences.length) {
        const intentSlugs = new Set(INTENTS.map((i) => i.value));
        this.form.preferences = user.preferences.filter((slug) => intentSlugs.has(slug));
      }
      if (user.name) {
        this.form.name = user.name;
      }
      if (user.email) {
        this.form.email = user.email;
      }

      if (!user.birth_date) {
        this.step = 1;
        return;
      }
      if (!user.location_id) {
        this.step = 2;
        return;
      }
      if (!user.gender || !user.preferred_gender || !user.search.age_from || !user.search.age_to) {
        this.step = 3;
        return;
      }
      if (!this.form.preferences) {
        this.step = 4;
        return;
      }

      this.step = 1;
    },
    prevStep() {
      if (this.step > 1) {
        this.step--;
      }
    },
    nextStep() {
      if (this.step >= TOTAL_STEPS) {
        if (!this.validateAccountStep()) {
          this.trackFormErrorsFromAccount();
          return;
        }
        this.submit();
        return;
      }
      if (!this.canAdvance) {
        this.trackStepGateError();
        return;
      }
      const completedStep = this.step;
      this.step++;
      trackEvent('sign_up_step', {
        step_number: completedStep,
        step_name: STEP_NAMES[completedStep] || `step_${completedStep}`,
        cta_location: this.ctaLocation,
      });
    },
    trackFormError(errorType) {
      trackEvent('form_error', {
        form_type: 'sign_up',
        step_number: this.step,
        error_type: errorType,
      });
    },
    trackStepGateError() {
      if (this.step === 1) {
        const age = calcAge(this.form.day, this.form.month, this.form.year);
        if (age === null) {
          this.trackFormError('birthday_invalid');
        } else if (age < 18) {
          this.trackFormError('underage');
        } else {
          this.trackFormError('birthday_invalid');
        }
      }
    },
    trackFormErrorsFromAccount() {
      if (this.errors.name) {
        this.trackFormError('name_invalid');
      }
      if (this.errors.email) {
        const msg = String(this.errors.email).toLowerCase();
        this.trackFormError(
          msg.includes('bereits') || msg.includes('taken') || msg.includes('unique')
            ? 'email_taken'
            : 'email_invalid'
        );
      }
      if (this.errors.password) {
        this.trackFormError('password_too_short');
      }
      if (this.errors.terms) {
        this.trackFormError('terms_required');
      }
    },
    validateAccountStep() {
      const next = {};
      const name = this.form.name.trim();

      if (!name) {
        next.name = this.t('err_name_required');
      }

      if (!this.isContinuing) {
        const email = this.form.email.trim();
        if (!email) {
          next.email = this.t('err_email_required');
        } else if (!EMAIL_RE.test(email)) {
          next.email = this.t('err_email_invalid');
        }

        if (!this.form.password) {
          next.password = this.t('err_password_required');
        } else if (!isPasswordValid(this.form.password)) {
          next.password = this.t('err_password_min');
        }

        if (!this.form.terms) {
          next.terms = this.t('err_terms_required');
        }
      }

      this.errors = next;
      this.submitError = '';
      return Object.keys(next).length === 0;
    },
    // PUT /register/update payload — shape mirrors what the legacy <auth-card>
    // step components send. `preferences` carries the numeric category IDs
    // (mapped from our intent slugs via preferenceSlugToId). If the catalog
    // fetch failed or the user picked an intent that's not in the catalog,
    // we send whatever did map; the dashboard's PreferencesCard handles any
    // gaps. Backend rule is `preferences: nullable|array`, so an empty list
    // is fine — it just means FLAG_PREFERENCES doesn't get set.
    buildUpdatePayload() {
      const pad = (n) => (String(n).length === 1 ? '0' + n : String(n));
      const birth_date = this.form.year + '-' + pad(this.form.month) + '-' + pad(this.form.day);
      const preferences = this.form.preferences
        .map((slug) => this.preferenceSlugToId[slug])
        .filter((id) => typeof id === 'number');
      return {
        name: this.form.name.trim(),
        gender: this.form.gender,
        preferred_gender:
          this.form.preferredGender === 'all' ? ['male', 'female'] : [this.form.preferredGender],
        birth_date,
        location_id: this.form.locationId,
        preferences,
        search: {
          age_from: this.form.ageFrom,
          age_to: this.form.ageTo,
        },
      };
    },
    async submit() {
      this.submitError = '';
      this.errors = {};
      this.submitting = true;
      this.$emit('submitting', true);
      await refreshCsrfToken();

      try {
        // Continue-mode skips POST /register (account already exists from
        // an earlier session) — only the fill-data PUT runs.
        let regResp = null;
        if (!this.isContinuing) {
          // 1) POST /register — creates the user + logs them in.
          //
          // cid/eid/campaign_query mirror the classic RegisterMixin.js payload —
          // they're captured into localStorage by front/scripts/custom.js when
          // the user lands with ?cid=&eid= and the backend uses them for
          // campaign attribution. Without them, v2 signups attribute as organic.
          await stashAnalyticsContext({
            cta_location: this.ctaLocation,
            method: 'email',
          });
          regResp = await axios.post(zRoute('register'), {
            email: this.form.email.trim(),
            password: this.form.password,
            cid: window.localStorage.getItem('campaign_cid'),
            eid: window.localStorage.getItem('campaign_eid'),
            campaign_query: window.localStorage.getItem('campaign_query'),
          });

          trackEvent('sign_up', {
            method: 'email',
            cta_location: this.ctaLocation,
          });
          this.$emit('account-created');
        }

        // 2) PUT /register/update — fills in everything we collected steps 1-4.
        // In continue mode this is the only call; any failure here must
        // surface to the user instead of being swallowed.
        try {
          await axios.put(zRoute('register.update'), this.buildUpdatePayload());
        } catch (e) {
          if (this.isContinuing) {
            throw e;
          }
          // New-signup path: the user IS registered. The standard fill-data
          // flow on /home picks up wherever this left off.
          console.warn('register.update failed, falling back to /home flow:', e);
        }

        // If the server says the email still needs verification, swap the
        // body to the "check your inbox" view and keep the modal open —
        // matches the classic flow (NameEmailLastCard → VerificationEmailCard).
        // Going straight to /home in this state would bounce back to the
        // landing because PageController@home requires hasVerifiedLogin().
        // We skip the submit-done success animation here because the user
        // hasn't actually finished — they still have to click the email link.
        const needsEmailVerify = !!(
          regResp &&
          regResp.data &&
          regResp.data.verification_is_required
        );
        if (needsEmailVerify) {
          this.awaitingEmailVerification = true;
          this.submitting = false;
          this.$emit('submitting', false);
          return;
        }

        this.$emit('submit-done');
        // Let the success animation play a moment before redirecting.
        setTimeout(() => {
          window.location.href = zRoute('home');
        }, 400);
      } catch (err) {
        this.submitting = false;
        this.$emit('submitting', false);
        const data = err && err.response && err.response.data;
        if (data && data.errors) {
          const next = {};
          if (data.errors.email) {
            next.email = data.errors.email[0];
            const msg = String(next.email).toLowerCase();
            this.trackFormError(
              msg.includes('bereits') || msg.includes('taken') || msg.includes('unique')
                ? 'email_taken'
                : 'email_invalid'
            );
          }
          if (data.errors.password) {
            next.password = data.errors.password[0];
            this.trackFormError('password_too_short');
          }
          if (data.errors.name) {
            next.name = data.errors.name[0];
            this.trackFormError('name_invalid');
          }
          this.errors = next;
        } else {
          this.submitError = (data && data.message) || this.t('submit_err_generic');
          this.trackFormError('submit_failed');
        }
      }
    },
  },
};
</script>

<style>
/* `.signup-register` exists only to give RegisterForm a single Vue root. We
   don't want it to break the modal's flex column layout, so `display:
   contents` makes it transparent — progress/body/footer behave as direct
   children of .signup-modal. */
.signup-register {
  display: contents;
}
</style>
